import type { SocialPostDraftCard, SocialPostPreviewCardMetadata } from '@wix/ai-scheduling-assistant-common-lib/entities';
import {
  contentHeldAt,
  contentKindOf,
  contentNotDrawn,
  contentPlacementAt,
  contentPublishedOf,
  contentStateOf,
  draftCardOf,
  readPostPlan,
  readPublishedPost,
  type BlogPostPreviewCard,
  type ContentState,
  type Delivery,
  type EmailPreviewCard,
  type PostPlan,
  type PublishedBlogPost,
  type PublishedPost,
  type SentEmail,
} from '@wix/ai-scheduling-assistant-common-lib/utils';
import { bucketOfTask, type BucketableTask } from '../agent-task-buckets';
import { compactPostPreview } from '../task-row-post-preview';
import { isScheduledTask, notBeforeMs } from '../task-schedule';
import { fromDayParam } from './calendar-dates';
import type { BookingClient, CalendarGuest, CalendarItem, CalendarPerson, CalendarPost, GoalTaskRow, GoalTasks } from './calendar-item';

// What the calendar draws, from each source's answer. The connected calendars and Bookings come from
// the harness already drawn (`/api/calendar/*`); what is left here is the part only the reader's own
// device can do (which local day an all-day item is on, the provider's page for that day) and the
// Symphony layers, whose rows every client already reads.

const HOUR = 3_600_000;

/** A meeting or a booking, as `/api/calendar/meetings` and `/api/calendar/bookings` send it. */
export interface WireCalendarItem {
  key: string;
  kind: 'meeting' | 'booking';
  title: string;
  /** ISO instants. */
  start: string;
  end: string;
  /** An all-day item's first day and the day after its last, as calendar dates. */
  allDay?: { startDate: string; endDate: string };
  sourceId: string;
  sourceLabel?: string;
  calendar?: { id: string; name: string; primary?: boolean };
  source?: { provider?: string; logoMediaId?: string; logoUrl?: string };
  color?: string;
  rsvp?: CalendarItem['rsvp'];
  booking?: { clients: BookingClient[] };
  needsYou?: boolean;
  settled?: boolean;
  status?: string;
  location?: string;
  people?: CalendarPerson[];
  hasGuests?: boolean;
  guests?: CalendarGuest[];
  guestCount?: number;
  description?: string;
  staff?: string[];
  staffResourceIds?: string[];
  capacity?: { booked: number; total: number };
  videoLink?: string;
  movable?: boolean;
  href?: string;
  external?: boolean;
  bookingUrl?: string;
  price?: { amount: string; currency: string };
  busy?: boolean;
  ref: Record<string, string>;
}

/** The provider's own calendar, opened on the reader's local day. */
function providerDayLink(provider: string | undefined, at: number): string | undefined {
  const day = new Date(at);
  if (provider === 'GOOGLE') {
    return `https://calendar.google.com/calendar/r/day/${day.getFullYear()}/${day.getMonth() + 1}/${day.getDate()}`;
  }
  if (provider === 'MICROSOFT') return 'https://outlook.live.com/calendar/';
  return undefined;
}

const MEDIA_ID = /^[A-Za-z0-9][A-Za-z0-9._~-]{0,255}$/;

/** Accepts the current identifier and the old trusted Wix URL, never another host. */
export function wixMediaId(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (MEDIA_ID.test(value)) return value;
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:' || url.hostname !== 'static.wixstatic.com' || !url.pathname.startsWith('/media/')) return undefined;
    const id = decodeURIComponent(url.pathname.slice('/media/'.length));
    return !id.includes('/') && MEDIA_ID.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}

function publicHttpsUrl(value: string | undefined): string | undefined {
  if (!value || value.length > 2048 || value !== value.trim()) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !!url.hostname && !url.username && !url.password ? value : undefined;
  } catch {
    return undefined;
  }
}

const AMOUNT = /^\d{1,9}(\.\d{1,4})?$/;
const CURRENCY = /^[A-Z]{3}$/;
/** A client's contact id is a GUID, as the server sends one; anything else is not an id to book again by. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function fixedPrice(price: WireCalendarItem['price']): price is { amount: string; currency: string } {
  return !!price && typeof price.amount === 'string' && AMOUNT.test(price.amount) && typeof price.currency === 'string' && CURRENCY.test(price.currency);
}

/** A wire item on the business clock. One that can't be placed is left out. */
export function fromWireItem(wire: WireCalendarItem, timeZone?: string): CalendarItem | undefined {
  const start = wire.allDay ? fromDayParam(wire.allDay.startDate, timeZone) : Date.parse(wire.start);
  const end = wire.allDay ? fromDayParam(wire.allDay.endDate, timeZone) : Date.parse(wire.end);
  if (typeof start !== 'number' || !Number.isFinite(start)) return undefined;
  const normalizedEnd = typeof end === 'number' && Number.isFinite(end) && end > start ? end : start + HOUR;
  const {
    allDay: _dates,
    people,
    booking,
    hasGuests,
    guests,
    guestCount,
    description,
    source,
    movable,
    href: _href,
    external: _external,
    bookingUrl: _bookingUrl,
    price,
    busy,
    ...rest
  } = wire;
  const safeGuests = wire.kind === 'meeting' ? guestsOf(guests) : [];
  const href =
    wire.kind === 'booking' ? undefined : wire.href ?? (wire.kind === 'meeting' ? providerDayLink(wire.source?.provider, start) : undefined);
  const logoMediaId = wixMediaId(source?.logoMediaId ?? source?.logoUrl);
  const safeSource =
    source && (source.provider || logoMediaId)
      ? { ...(source.provider ? { provider: source.provider } : {}), ...(logoMediaId ? { logoMediaId } : {}) }
      : undefined;
  const clientNames = people?.flatMap((person) => (person.name ? [{ name: person.name }] : []));
  const safeBooking = booking
    ? {
        clients: booking.clients.map(({ id, revision, name, status, payment, paymentOption, attendance, bookedBy, contactId }) => ({
          ...(id ? { id } : {}),
          ...(revision ? { revision } : {}),
          ...(name ? { name } : {}),
          ...(status ? { status } : {}),
          ...(payment ? { payment } : {}),
          ...(paymentOption ? { paymentOption } : {}),
          ...(attendance ? { attendance } : {}),
          ...(bookedBy ? { bookedBy } : {}),
          ...(contactId && GUID.test(contactId) ? { contactId } : {}),
        })),
      }
    : undefined;
  return {
    ...rest,
    start,
    end: normalizedEnd,
    allDay: !!wire.allDay,
    ...(wire.allDay ? { allDayStartDate: wire.allDay.startDate, allDayEndDate: wire.allDay.endDate } : {}),
    ...(safeSource ? { source: safeSource } : {}),
    ...(wire.kind === 'meeting' && (hasGuests || (people?.length ?? 0) > 0) ? { hasGuests: true } : {}),
    ...(wire.kind === 'meeting' && movable === true ? { movable: true } : {}),
    ...(wire.kind === 'meeting' && busy === true ? { busy: true } : {}),
    ...(safeGuests.length ? { guests: safeGuests, guestCount: Math.max(safeGuests.length, typeof guestCount === 'number' ? guestCount : 0) } : {}),
    ...(wire.kind === 'meeting' && typeof description === 'string' && description.trim() ? { description: description.slice(0, DESCRIPTION_MAX) } : {}),
    ...(wire.kind === 'booking' && clientNames?.length ? { people: clientNames } : {}),
    ...(wire.kind === 'booking' && safeBooking ? { booking: safeBooking } : {}),
    ...(wire.kind === 'booking' && publicHttpsUrl(wire.bookingUrl) ? { bookingUrl: wire.bookingUrl } : {}),
    ...(wire.kind === 'booking' && fixedPrice(price) ? { price: { amount: price.amount, currency: price.currency } } : {}),
    ...(href ? { href, external: true } : {}),
  };
}

/**
 * The booking in the harness's one-booking reply (`GET v1/calendar/booking`, `{ contractVersion, booking: <item> | null }`),
 * read as the bookings read reads its items; nothing for no booking, or for a reply that is not that shape.
 */
export function bookingFromReply(reply: unknown): CalendarItem | undefined {
  if (!reply || typeof reply !== 'object' || Array.isArray(reply)) return undefined;
  const booking = (reply as { booking?: unknown }).booking;
  if (!booking || typeof booking !== 'object' || Array.isArray(booking)) return undefined;
  const wire = booking as WireCalendarItem;
  if (wire.kind !== 'booking' || typeof wire.key !== 'string' || typeof wire.start !== 'string' || !wire.ref || typeof wire.ref !== 'object') return undefined;
  return fromWireItem(wire);
}

/** The fields of a task the calendar reads. `WebTask` satisfies it. */
export type CalendarTask = BucketableTask & {
  id: string;
  title?: string;
  workspaceId?: string;
  updatedDate?: string | Date;
  agentId?: string;
  /** The routine whose run opened the task; its work is the routine's, which the routine's run already stands for. */
  pulseIdentityId?: string;
  goalId?: string;
  subtitle?: string;
  summary?: string;
  /** When it finished, as both task reads derive it from its change log (`finishedAtOf`); `null` when a sweep closed it. */
  finishedAt?: string | null;
  /** What it finished with, as both task reads derive it (`finishedResultOf`); absent when its summary is still a question. */
  finishedResult?: string;
  /** The task's drafted social post, in every state (`/api/tasks`). */
  socialPostPreviewCard?: SocialPostPreviewCardMetadata;
  socialPostDraftCard?: SocialPostDraftCard;
  /** The planned post's idea before it is drafted. */
  postPlan?: PostPlan;
  /** The post once it went out. */
  publishedPost?: PublishedPost;
  /** `display.origin`: `saved-action` on a post approved ahead. */
  displayOrigin?: string;
  /** The task's decision edges, with the answer stamped back on each. */
  blockedByDecisions?: readonly { decisionId?: string; resolvedChoice?: string }[];
  emailPreviewCard?: EmailPreviewCard;
  blogPostPreviewCard?: BlogPostPreviewCard;
  sentEmail?: SentEmail;
  publishedBlogPost?: PublishedBlogPost;
  delivery?: Delivery;
  savedActionId?: string;
  prepareNowAt?: string;
  replacedBy?: string;
};

const RESULT_MAX = 300;

/**
 * A finished task's result, as the task reads send it (`finishedResult`): its summary only when that summary is a result,
 * not a question it last asked its owner, so "What happened" never shows a hand-back's question.
 */
export function agentResult(task: Pick<CalendarTask, 'finishedResult'>): string | undefined {
  const text = task.finishedResult?.trim() ?? '';
  return text ? text.slice(0, RESULT_MAX) : undefined;
}

/** How far back finished work is drawn: the phones' task read holds the last 30 days, so all three draw the same. */
const FINISHED_WINDOW_MS = 30 * 86_400_000;


/** The drafted post as `/api/tasks` reads it (`readSocialPostPreviewCard`); a value without its decision or media list is none. */
function cardOf(card: SocialPostPreviewCardMetadata | undefined): SocialPostPreviewCardMetadata | undefined {
  return card && typeof card === 'object' && typeof card.decisionId === 'string' && card.decisionId && Array.isArray(card.media) ? card : undefined;
}

function draftOf(task: CalendarTask): SocialPostDraftCard | undefined {
  const draft = cardOf(task.socialPostPreviewCard) ? undefined : task.socialPostDraftCard;
  return draft && typeof draft === 'object' && typeof draft.draftId === 'string' && draft.draftId && Array.isArray(draft.media) ? draft : undefined;
}

/** The task's `display` as the content readers read it, rebuilt from the fields the task reads carry. */
function contentDisplay(task: CalendarTask, card: SocialPostPreviewCardMetadata | undefined): Record<string, unknown> {
  const publishedPost = readPublishedPost({ publishedPost: task.publishedPost }) ?? undefined;
  const postAt = task.postAt ? new Date(task.postAt) : undefined;
  return {
    ...(card ? { socialPostPreviewCard: card } : {}),
    ...(task.postPlan ? { postPlan: task.postPlan } : {}),
    ...(task.delivery ? { delivery: task.delivery } : {}),
    ...(task.savedActionId ? { savedActionId: task.savedActionId } : {}),
    ...(publishedPost ? { publishedPost } : {}),
    ...(task.displayOrigin ? { origin: task.displayOrigin } : {}),
    ...(task.emailPreviewCard ? { emailPreviewCard: task.emailPreviewCard } : {}),
    ...(task.blogPostPreviewCard ? { blogPostPreviewCard: task.blogPostPreviewCard } : {}),
    ...(task.sentEmail ? { sentEmail: task.sentEmail } : {}),
    ...(task.publishedBlogPost ? { publishedBlogPost: task.publishedBlogPost } : {}),
    ...(task.prepareNowAt ? { prepareNowAt: task.prepareNowAt } : {}),
    ...(task.replacedBy ? { replacedBy: task.replacedBy } : {}),
    ...(!task.delivery?.publishAt && postAt && !Number.isNaN(postAt.getTime()) ? { marketingPlanPost: { postAt: postAt.toISOString() } } : {}),
  };
}

/**
 * The task's post for its row, or `undefined` when it carries none. The card is read as it is, whatever its
 * question's state: `postPreviewHoldsDecision` is the QUESTION card's gate (it hides the post once answered),
 * and the calendar shows the post through every state.
 */
function taskPost(task: CalendarTask, display: Record<string, unknown>, now: number): CalendarPost | undefined {
  const kind = contentKindOf(display);
  if (!kind) return undefined;
  const plan = readPostPlan(display) ?? undefined;
  const state = contentStateOf({ status: task.status, blockedByDecisions: task.blockedByDecisions, display }, now);
  const url = state === 'done' ? contentPublishedOf(display)?.url : undefined;
  if (kind !== 'social') {
    const draft = draftCardOf(display, kind);
    const headline = draft ? ((kind === 'email' ? (draft as EmailPreviewCard).subject : '') || draft.title) : '';
    const idea = !draft && plan?.concept ? { concept: plan.concept } : undefined;
    return {
      kind,
      ...(state ? { state } : {}),
      ...(draft ? { draft: { title: headline } } : {}),
      ...(idea ? { idea } : {}),
      ...(url ? { url } : {}),
    };
  }
  const card = display.socialPostPreviewCard as SocialPostPreviewCardMetadata | undefined;
  const idea =
    !card && plan
      ? {
          ...(plan.network ? { network: plan.network } : {}),
          ...(plan.format ? { format: plan.format } : {}),
          ...(plan.concept ? { concept: plan.concept } : {}),
        }
      : undefined;
  return {
    kind,
    ...(state ? { state } : {}),
    ...(card ? { preview: compactPostPreview(card) } : {}),
    ...(idea ? { idea } : {}),
    ...(url ? { url } : {}),
  };
}

const CONTENT_STATUS: Partial<Record<ContentState, string>> = {
  idea: 'scheduled',
  drafting: 'inProgress',
  working: 'inProgress',
  ready: 'waiting',
  scheduled: 'scheduled',
  done: 'done',
};

function itemStatus(task: CalendarTask, bucket: ReturnType<typeof bucketOfTask>, post: CalendarPost | undefined, now: number): string {
  const content = post?.state ? CONTENT_STATUS[post.state] : undefined;
  if (content) return content;
  if (bucket === 'waiting') return bucket;
  return isScheduledTask(task, now) ? 'scheduled' : bucket;
}

/**
 * A marketing task (social, email or blog) sits where its delivery says, whatever its status: when it went out, else
 * when it is set to go out, else the older records (a published post, a plan post's time); dropped, failed and replaced
 * ones stay out. Anything else follows the rules below. Pending work with a real schedule sits at `notBefore`. An
 * agent's task that finished sits where it finished, marked done, at the `finishedAt` its task read sends, and only
 * within the last 30 days. A task a sweep closed (`null`), one with no finish, and any other settled task, stay out. A
 * task a routine's run opened (`pulseIdentityId`) is drawn only while it waits on the owner or carries a post, as the
 * routine's work (`ref.routineId`, no `agentId`); once finished, at its finish and without the check, since the
 * routine's run is the work done and is counted.
 */
export function taskItems(tasks: CalendarTask[], now: number = Date.now()): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const task of tasks) {
    if (!task?.id) continue;
    const bucket = bucketOfTask(task);
    const settled = bucket === 'done' || bucket === 'failed';
    const draft = draftOf(task);
    const display = contentDisplay(task, cardOf(task.socialPostPreviewCard) ?? draft);
    const post = taskPost(task, display, now);
    const routineId = task.pulseIdentityId;
    const ref = routineId ? { taskId: task.id, routineId, goalId: task.goalId } : { taskId: task.id, agentId: task.agentId, goalId: task.goalId };
    if (post && contentNotDrawn({ status: task.status, blockedByDecisions: task.blockedByDecisions, display }, now)) continue;
    const opensTask = !!readPostPlan({ postPlan: task.postPlan });
    const draftAt = draft?.scheduledAt && !contentPublishedOf(display) && !contentHeldAt(display) ? draft.scheduledAt : undefined;
    const placedAt = post ? Date.parse(draftAt ?? contentPlacementAt(display) ?? '') : NaN;
    if (Number.isFinite(placedAt)) {
      const result = task.status === 'COMPLETED' && !routineId ? agentResult(task) : undefined;
      items.push({
        key: `task:${task.id}`,
        kind: 'task',
        title: task.title || '',
        start: placedAt,
        end: placedAt,
        allDay: false,
        needsYou: bucket === 'waiting',
        settled,
        status: itemStatus(task, bucket, post, now),
        ...(post?.state === 'done' && !routineId ? { done: true } : {}),
        ...(result ? { result } : {}),
        workspaceId: task.workspaceId || undefined,
        href: `/tasks/${encodeURIComponent(task.id)}`,
        ...(post ? { post } : {}),
        ...(opensTask ? { opensTask } : {}),
        ref,
      });
      continue;
    }
    // A routine's work is drawn as the routine's run, and wears no agent's face: drawn here too it would be the same
    // work twice, counted twice, under the agent that happened to run it. What waits on the owner is drawn, since the
    // owner must act on it and the "Waiting on you" pill counts it, and so is a post, which the run does not show.
    if (routineId && bucket !== 'waiting' && !post) continue;
    if (task.status === 'COMPLETED' && (routineId || task.agentId)) {
      const finishedAt = Date.parse(task.finishedAt ?? '');
      if (!Number.isFinite(finishedAt) || finishedAt < now - FINISHED_WINDOW_MS) continue;
      const result = routineId ? undefined : agentResult(task);
      items.push({
        key: `task:${task.id}`,
        kind: 'task',
        title: task.title || '',
        start: finishedAt,
        end: finishedAt,
        allDay: false,
        needsYou: false,
        settled: true,
        status: 'done',
        ...(routineId ? {} : { done: true }),
        ...(result ? { result } : {}),
        workspaceId: task.workspaceId || undefined,
        href: `/tasks/${encodeURIComponent(task.id)}`,
        ...(post ? { post } : {}),
        ...(opensTask ? { opensTask } : {}),
        ref,
      });
      continue;
    }
    if (settled) continue;
    const at = notBeforeMs(task);
    if (at == null || !Number.isFinite(at)) continue;
    items.push({
      key: `task:${task.id}`,
      kind: 'task',
      title: task.title || '',
      start: at,
      end: at,
      allDay: false,
      needsYou: bucket === 'waiting',
      settled,
      status: itemStatus(task, bucket, post, now),
      workspaceId: task.workspaceId || undefined,
      href: `/tasks/${encodeURIComponent(task.id)}`,
      ...(post ? { post } : {}),
      ...(opensTask ? { opensTask } : {}),
      ref,
    });
  }
  return items;
}

/** The fields of a routine the calendar reads: a `WebRoutine` read with a window, which lists its runs. */
export interface CalendarRoutine {
  id: string;
  name?: string;
  workspaceId?: string;
  cron?: string;
  /** Its fire times in the window asked for, as ISO instants, by the runtime's own day rule. */
  runs?: string[];
  runsEveryDay?: boolean;
  /** Its last real run, ISO, and the tasks that run started. */
  lastRun?: string | null;
  lastRunTaskCount?: number;
}

/** One routine's runs in the range asked for, as `/api/calendar/routine-runs` answers them, split by the server's clock. */
export interface RoutineRunsOf {
  routineId: string;
  /** Its cron fire times from the server's now (`readAt`) on, as ISO instants. */
  upcoming: string[];
  /** Its recorded run starts before `readAt`, whatever its state: paused, trigger-only and ended routines' runs too. */
  done: { at: string; runId?: string }[];
  repeatsDaily: boolean;
}

/** The routines' runs in the range on screen (`/api/calendar/routine-runs`), drawn as they come. */
export interface RoutineRuns {
  routines: RoutineRunsOf[];
  /** ISO instant of the server's now, where it split done from upcoming. */
  readAt: string;
  /** ISO instant the run read reached back to when it stopped at its bound; absent when it covered the whole range. */
  coveredFrom?: string;
}

/**
 * Each routine's runs. Given `runs` (the Calendar reads them), a routine draws its `upcoming` fire times as runs to come
 * and its `done` runs where they started, marked done, as the server split them; a routine the answer leaves out draws
 * nothing, and `null` (no answer yet, or a failed one) draws nothing at all. Without `runs` (a read that never asks for
 * them), every fire time in the window the routine was read for is drawn. A daily one is marked so the month grid
 * leaves it out.
 */
export function routineItems(routines: CalendarRoutine[], runs?: RoutineRuns | null): CalendarItem[] {
  const items: CalendarItem[] = [];
  const answered = runs ? new Map(runs.routines.map((each) => [each.routineId, each])) : undefined;
  for (const routine of routines) {
    if (!routine?.id) continue;
    const lastRun = Date.parse(routine.lastRun ?? '');
    const answer = answered?.get(routine.id);
    const repeatsDaily = runs === undefined ? routine.runsEveryDay === true : answer?.repeatsDaily === true;
    const item = (at: number, done: boolean): CalendarItem => ({
      key: `routine:${routine.id}:${at}`,
      kind: 'routine',
      title: routine.name || '',
      start: at,
      end: at,
      allDay: false,
      repeatsDaily,
      ...(done ? { done: true } : {}),
      // The routine's last run describes a run still to come; on a run that already happened it would name another run.
      ...(!done && Number.isFinite(lastRun) ? { lastRun } : {}),
      ...(!done && Number.isFinite(lastRun) && typeof routine.lastRunTaskCount === 'number' ? { lastRunTaskCount: routine.lastRunTaskCount } : {}),
      workspaceId: routine.workspaceId || undefined,
      ref: { routineId: routine.id, cron: routine.cron },
    });
    const upcoming = runs === undefined ? routine.runs ?? [] : answer?.upcoming ?? [];
    for (const run of upcoming) {
      const at = Date.parse(run);
      if (Number.isFinite(at)) items.push(item(at, false));
    }
    for (const run of answer?.done ?? []) {
      const at = Date.parse(run.at);
      if (Number.isFinite(at)) items.push(item(at, true));
    }
  }
  return items;
}

export interface CalendarGoal {
  id: string;
  title?: string;
  status?: string;
  outcome?: string;
  workspaceId?: string;
  endsAt?: string;
}

const GOAL_CONTENT_STATUS: Partial<Record<ContentState, GoalTaskRow['status']>> = {
  idea: 'idea',
  drafting: 'inProgress',
  working: 'inProgress',
  ready: 'waiting',
  scheduled: 'scheduled',
  done: 'done',
  failed: 'failed',
};

/**
 * A goal's own tasks for its deadline: each with one status and its scheduled date, or none. Dated tasks
 * come first in date order, then the undated ones; a task is listed once however many reads carried it.
 * Only tasks group under a goal; a routine stays separate even when it serves one.
 */
export function goalTasks(goalId: string, tasks: CalendarTask[], now: number = Date.now()): GoalTasks {
  const seen = new Set<string>();
  const rows: GoalTaskRow[] = [];
  for (const task of tasks) {
    if (!task?.id || task.goalId !== goalId || seen.has(task.id)) continue;
    seen.add(task.id);
    const bucket = bucketOfTask(task);
    const draft = draftOf(task);
    const display = contentDisplay(task, cardOf(task.socialPostPreviewCard) ?? draft);
    const content = contentKindOf(display) ? contentStateOf({ status: task.status, blockedByDecisions: task.blockedByDecisions, display }, now) : undefined;
    const status: GoalTaskRow['status'] = content ? GOAL_CONTENT_STATUS[content] ?? bucket : bucket === 'inProgress' && isScheduledTask(task, now) ? 'scheduled' : bucket;
    const publishAt = Date.parse(task.delivery?.publishAt ?? '');
    const goOut = task.postAt ? new Date(task.postAt).getTime() : NaN;
    const at = Number.isFinite(publishAt) ? publishAt : Number.isFinite(goOut) ? goOut : notBeforeMs(task);
    rows.push({ taskId: task.id, title: task.title || '', status, ...(at != null && Number.isFinite(at) ? { at } : {}) });
  }
  rows.sort((a, b) => (a.at ?? Infinity) - (b.at ?? Infinity) || a.title.localeCompare(b.title) || a.taskId.localeCompare(b.taskId));
  return { done: rows.filter((row) => row.status === 'done').length, total: rows.length, tasks: rows };
}

/** Open goals with a real end date appear at that recorded deadline; settled and undated goals do not. */
export function goalItems(goals: CalendarGoal[], tasks: CalendarTask[] = [], now: number = Date.now()): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const goal of goals) {
    if (!goal?.id || goal.outcome || goal.status === 'ACHIEVED' || goal.status === 'ABANDONED' || goal.status === 'CANCELLED') continue;
    const at = Date.parse(goal.endsAt ?? '');
    if (!Number.isFinite(at)) continue;
    items.push({
      key: `goal:${goal.id}`,
      kind: 'goal',
      title: goal.title || '',
      start: at,
      end: at,
      allDay: false,
      workspaceId: goal.workspaceId || undefined,
      href: `/goals?goal=${encodeURIComponent(goal.id)}`,
      goalTasks: goalTasks(goal.id, tasks, now),
      ref: { goalId: goal.id },
    });
  }
  return items;
}

const DESCRIPTION_MAX = 2000;
const GUEST_RSVP = new Set(['accepted', 'declined', 'tentative', 'pending']);

/** Each guest the server sent, its fields checked; one with neither a name nor an email is left out. */
function guestsOf(guests: unknown): CalendarGuest[] {
  if (!Array.isArray(guests)) return [];
  return guests.flatMap((guest) => {
    if (!guest || typeof guest !== 'object') return [];
    const { name, email, rsvp, organizer, self } = guest as Record<string, unknown>;
    if (!(typeof name === 'string' && name) && !(typeof email === 'string' && email)) return [];
    return [
      {
        ...(typeof name === 'string' && name ? { name } : {}),
        ...(typeof email === 'string' && email ? { email } : {}),
        ...(typeof rsvp === 'string' && GUEST_RSVP.has(rsvp) ? { rsvp: rsvp as CalendarGuest['rsvp'] } : {}),
        ...(organizer === true ? { organizer: true } : {}),
        ...(self === true ? { self: true } : {}),
      },
    ];
  });
}
